/**
 * Torso segment tracking with explicit intermediate points
 * BETWEEN shoulders and hips (not only the four joint endpoints).
 *
 * Pure geometry — no DOM / camera. Suitable for measuring stack vs
 * hollow/banana deviation along densified left, right, and midline chains.
 */

import type { Point, PoseLandmark, TorsoSegment } from "./types";
import { LM } from "./landmarks";

export const DEFAULT_INTERMEDIATE_COUNT = 5;

export function midpoint(a: Point, b: Point): Point {
  return {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
    z: ((a.z ?? 0) + (b.z ?? 0)) / 2,
    visibility: Math.min(a.visibility ?? 1, b.visibility ?? 1),
  };
}

export function lerpPoint(a: Point, b: Point, t: number): Point {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: (a.z ?? 0) + ((b.z ?? 0) - (a.z ?? 0)) * t,
    visibility: Math.min(a.visibility ?? 1, b.visibility ?? 1),
  };
}

export function sub(a: Point, b: Point): Point {
  return { x: a.x - b.x, y: a.y - b.y, z: (a.z ?? 0) - (b.z ?? 0) };
}

export function add(a: Point, b: Point): Point {
  return { x: a.x + b.x, y: a.y + b.y, z: (a.z ?? 0) + (b.z ?? 0) };
}

export function scale(a: Point, s: number): Point {
  return { x: a.x * s, y: a.y * s, z: (a.z ?? 0) * s };
}

export function length2(a: Point): number {
  return Math.hypot(a.x, a.y);
}

export function length3(a: Point): number {
  return Math.hypot(a.x, a.y, a.z ?? 0);
}

export function normalize2(a: Point): Point {
  const len = length2(a);
  if (len < 1e-9) return { x: 0, y: 0, z: a.z ?? 0 };
  return { x: a.x / len, y: a.y / len, z: a.z ?? 0 };
}

/** Perpendicular in image plane (rotate 90° CCW). */
export function perp2(a: Point): Point {
  return { x: -a.y, y: a.x, z: a.z ?? 0 };
}

export function dot2(a: Point, b: Point): number {
  return a.x * b.x + a.y * b.y;
}

/**
 * Densify a segment from shoulder endpoint → hip endpoint.
 * Returns [shoulder, i1, i2, …, iN, hip] where N = intermediateCount.
 * Intermediate points are strictly between the endpoints.
 */
export function densifyChain(
  shoulder: Point,
  hip: Point,
  intermediateCount: number = DEFAULT_INTERMEDIATE_COUNT,
  /** Optional per-intermediate offsets in the same frame (length = intermediateCount). */
  intermediateOffsets?: Point[],
): Point[] {
  if (intermediateCount < 1) {
    throw new Error("intermediateCount must be >= 1 to produce between-shoulder-hip points");
  }
  const points: Point[] = [shoulder];
  const steps = intermediateCount + 1;
  for (let i = 1; i <= intermediateCount; i++) {
    const t = i / steps;
    let p = lerpPoint(shoulder, hip, t);
    if (intermediateOffsets && intermediateOffsets[i - 1]) {
      p = add(p, intermediateOffsets[i - 1]);
    }
    points.push(p);
  }
  points.push(hip);
  return points;
}

/**
 * Intermediate-only slice (excludes endpoints).
 */
export function intermediatePoints(chain: Point[]): Point[] {
  if (chain.length < 3) return [];
  return chain.slice(1, -1);
}

/**
 * Build left / right / midline torso segments with densified intermediates
 * from the four shoulder/hip landmarks.
 *
 * Optionally applies a signed spine curvature so intermediate midline points
 * leave the shoulder–hip chord (positive ≈ banana/arch, negative ≈ hollow).
 * Offsets use sin(π t) so endpoints stay fixed and mid-chain peaks.
 */
export function buildTorsoSegment(
  leftShoulder: Point,
  rightShoulder: Point,
  leftHip: Point,
  rightHip: Point,
  options: {
    intermediateCount?: number;
    /** Signed curvature amplitude in normalized image units. */
    spineCurvature?: number;
    leftOffsets?: Point[];
    rightOffsets?: Point[];
    midOffsets?: Point[];
  } = {},
): TorsoSegment {
  const n = options.intermediateCount ?? DEFAULT_INTERMEDIATE_COUNT;
  const shoulderMid = midpoint(leftShoulder, rightShoulder);
  const hipMid = midpoint(leftHip, rightHip);

  let midOffsets = options.midOffsets;
  if (midOffsets === undefined && options.spineCurvature !== undefined) {
    midOffsets = curvatureOffsets(shoulderMid, hipMid, n, options.spineCurvature);
  }

  // Lateral chains get a fraction of midline curvature so the mesh bows together.
  let leftOffsets = options.leftOffsets;
  let rightOffsets = options.rightOffsets;
  if (options.spineCurvature !== undefined && leftOffsets === undefined) {
    leftOffsets = curvatureOffsets(leftShoulder, leftHip, n, options.spineCurvature * 0.85);
  }
  if (options.spineCurvature !== undefined && rightOffsets === undefined) {
    rightOffsets = curvatureOffsets(rightShoulder, rightHip, n, options.spineCurvature * 0.85);
  }

  return {
    left: densifyChain(leftShoulder, leftHip, n, leftOffsets),
    right: densifyChain(rightShoulder, rightHip, n, rightOffsets),
    midline: densifyChain(shoulderMid, hipMid, n, midOffsets),
    intermediateCount: n,
  };
}

/**
 * sin(π t) offsets perpendicular to the shoulder→hip chord.
 * Positive curvature offsets toward +perp (caller defines semantic side).
 */
export function curvatureOffsets(
  shoulder: Point,
  hip: Point,
  intermediateCount: number,
  amplitude: number,
): Point[] {
  const chord = sub(hip, shoulder);
  const normal = normalize2(perp2(chord));
  const steps = intermediateCount + 1;
  const offsets: Point[] = [];
  for (let i = 1; i <= intermediateCount; i++) {
    const t = i / steps;
    const w = Math.sin(Math.PI * t) * amplitude;
    offsets.push(scale(normal, w));
  }
  return offsets;
}

/**
 * Extract torso from a full MediaPipe-style landmark array (length >= 25).
 *
 * Live tracking defaults to **linear** densification along the true
 * shoulder→hip segments so intermediate points sit on the body. Pass an
 * explicit `spineCurvature` (demos/tests) to bow the mid-chain off the chord.
 *
 * Banana/hollow scoring without fake visual offsets uses
 * `estimateSpineCurvature` / joint geometry in the metrics layer.
 */
export function torsoFromLandmarks(
  landmarks: PoseLandmark[],
  options: {
    intermediateCount?: number;
    /** When omitted, 0 — intermediates stay on the shoulder–hip lines. */
    spineCurvature?: number;
  } = {},
): TorsoSegment {
  requireLandmarks(landmarks);
  const ls = landmarks[LM.LEFT_SHOULDER];
  const rs = landmarks[LM.RIGHT_SHOULDER];
  const lh = landmarks[LM.LEFT_HIP];
  const rh = landmarks[LM.RIGHT_HIP];

  const curvature = options.spineCurvature ?? 0;

  return buildTorsoSegment(ls, rs, lh, rh, {
    intermediateCount: options.intermediateCount,
    spineCurvature: curvature,
  });
}

/**
 * Coarse signed spine / stack-arch estimate from available joints.
 * Positive ≈ banana/arch tendency, negative ≈ hollow.
 * Used for coaching metrics when intermediate points are collinear on the chord.
 */
export function estimateSpineCurvature(landmarks: PoseLandmark[]): number {
  requireLandmarks(landmarks);
  const ls = landmarks[LM.LEFT_SHOULDER];
  const rs = landmarks[LM.RIGHT_SHOULDER];
  const lh = landmarks[LM.LEFT_HIP];
  const rh = landmarks[LM.RIGHT_HIP];
  const shoulderMid = midpoint(ls, rs);
  const hipMid = midpoint(lh, rh);
  const chord = sub(hipMid, shoulderMid);
  const chordLen = length2(chord);
  if (chordLen < 1e-6) return 0;

  const lw = landmarks[LM.LEFT_WRIST];
  const rw = landmarks[LM.RIGHT_WRIST];
  const lk = landmarks[LM.LEFT_KNEE];
  const rk = landmarks[LM.RIGHT_KNEE];
  const la = landmarks[LM.LEFT_ANKLE];
  const ra = landmarks[LM.RIGHT_ANKLE];

  let signed = 0;

  // Hip mid offset from the wrist→shoulder stack line (classic banana dump).
  if (lw && rw) {
    const wristMid = midpoint(lw, rw);
    const stack = sub(shoulderMid, wristMid);
    const stackLen = length2(stack);
    if (stackLen > 1e-6) {
      const n = normalize2(perp2(stack));
      // How far hips leave the arm stack, normalized by torso length.
      signed += (dot2(sub(hipMid, shoulderMid), n) / chordLen) * 0.12;
    }
  }

  // Over-extension at the hip: shoulder–hip–knee angle past 180° proxy via
  // knee sitting on the arch side of the torso chord.
  if (lk && rk) {
    const kneeMid = midpoint(lk, rk);
    const n = normalize2(perp2(chord));
    signed += (dot2(sub(kneeMid, hipMid), n) / Math.max(chordLen, 1e-6)) * 0.08;
  }

  // Ankles continuing the same arch (full-line banana).
  if (la && ra) {
    const ankleMid = midpoint(la, ra);
    const n = normalize2(perp2(chord));
    signed += (dot2(sub(ankleMid, hipMid), n) / Math.max(chordLen, 1e-6)) * 0.05;
  }

  // Depth cue when MediaPipe provides z.
  const zShoulder = ((ls.z ?? 0) + (rs.z ?? 0)) / 2;
  const zHip = ((lh.z ?? 0) + (rh.z ?? 0)) / 2;
  signed += (zHip - zShoulder) * 0.15;

  return Math.max(-0.2, Math.min(0.2, signed));
}

function requireLandmarks(landmarks: PoseLandmark[]): void {
  if (!landmarks || landmarks.length < 25) {
    throw new Error("Pose landmarks array must include shoulders and hips (index 0–24)");
  }
}

/**
 * Apply an additional curvature to an existing torso segment's intermediate
 * points (endpoints fixed). Used by tests and offline tooling.
 */
export function applySpineCurvature(
  torso: TorsoSegment,
  amplitude: number,
): TorsoSegment {
  const n = torso.intermediateCount;
  const shoulderMid = torso.midline[0];
  const hipMid = torso.midline[torso.midline.length - 1];
  const midOff = curvatureOffsets(shoulderMid, hipMid, n, amplitude);

  const leftShoulder = torso.left[0];
  const leftHip = torso.left[torso.left.length - 1];
  const rightShoulder = torso.right[0];
  const rightHip = torso.right[torso.right.length - 1];

  return {
    left: densifyChain(
      leftShoulder,
      leftHip,
      n,
      curvatureOffsets(leftShoulder, leftHip, n, amplitude * 0.85),
    ),
    right: densifyChain(
      rightShoulder,
      rightHip,
      n,
      curvatureOffsets(rightShoulder, rightHip, n, amplitude * 0.85),
    ),
    midline: densifyChain(shoulderMid, hipMid, n, midOff),
    intermediateCount: n,
  };
}
